<script setup lang="ts">
import { AccessFeature } from "@/shared/access-control/access-control";
import ShowIfHasAccess from "@/shared/access-control/components/show-if-has-access.vue";
import QitBtn from "@/shared/components/buttons/qit-button.vue";
import WidgetCard from "@/shared/components/widgets/widget-card.vue";
import { useAbilityRoute } from "@/shared/environment/ability-route";
import { encodeParamArrayToString } from "@/shared/environment/ability-route-helper";
import { RouteElementPathWithParams } from "@/shared/environment/ability.types";
import { CommonRouteAliases } from "@/shared/environment/common-route-aliases";
import { MechanicArticleContext } from "@/shared/environment/widgets/widget-context";
import { useScreenSize } from "@/shared/screen/composables/screen-size";
import { computed } from "vue";

const props = defineProps<{
    widgetContext: MechanicArticleContext;
}>();

const { createRouteFromRouteElements } = useAbilityRoute();
const { isXSmall } = useScreenSize();

const combinedViewPath = computed(() => {
    const parentRoute: RouteElementPathWithParams = [
        {
            alias: CommonRouteAliases.product,
            namedParams: { [CommonRouteAliases.product]: props.widgetContext.product },
        },
    ];

    if (props.widgetContext.asset) {
        parentRoute.push({
            alias: CommonRouteAliases.asset,
            namedParams: { [CommonRouteAliases.asset]: props.widgetContext.asset },
        });
    }

    // widgetContext.mechanic already carries the encoded assembly path from the current route.
    // Fall back to root if not available (e.g. navigation without mechanic context).
    const encodedAssemblyPath = props.widgetContext.mechanic || encodeParamArrayToString([""]);

    return createRouteFromRouteElements(
        parentRoute,
        {
            alias: CommonRouteAliases.mechanicCombinedView,
            namedParams: {
                [CommonRouteAliases.mechanicCombinedView]: encodedAssemblyPath,
            },
        },
        { selectedMechanicID: props.widgetContext.mechanicId }
    );
});
</script>

<template>
    <ShowIfHasAccess v-if="!isXSmall" :feature-id="AccessFeature.combined_view">
        <div class="row q-col-gutter-lg">
            <div class="col-12 col-md-6">
                <WidgetCard
                    icon="fa-light icon-light-color fa-table-layout q-mr-sm"
                    :title="$t('mechanic.enter_combined_view_widget_title')"
                    :description="$t('mechanic.enter_combined_view_widget_description')"
                >
                    <template #button>
                        <router-link class="router-link flex flex-end" :to="combinedViewPath">
                            <QitBtn
                                color="secondary"
                                data-qs="enter-combined-view-mechanic-article-widget-button"
                                :label="$t('mechanic.enter_combined_view_widget_button_text')"
                                :disabled="false"
                            />
                        </router-link>
                    </template>
                </WidgetCard>
            </div>
        </div>
    </ShowIfHasAccess>
</template>
